import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { NavBar, Image } from 'antd-mobile';
import { getNavBarContainerStyle } from '../../../../utils/deviceUtils';
import { formatTimestampByLanguage } from '../../../../utils/dateTimeUtils';
import { buildAddress1AndAddress2 } from '../../../../utils/addressUtils';
import i18n, {t} from '../../../../i18n';
import { TmsPath } from '../../../../enums/TmsNavigatePathEnum';
import { TmsTaskWayPointWithAddressType } from '../../graphql/queries/getTmsPublishedTaskByDriverByDate';
import { useTransportStore } from '../../store/transportStore';
import TransportPhotoViewer from '../../components/TransportPhotoViewer';

const TransportFinishedDetailPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { taskWayPointId, taskId, selectedDate } = useParams<{
        taskWayPointId: string;
        taskId: string;
        selectedDate: string;
    }>();
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [waypointData, setWaypointData] = useState<TmsTaskWayPointWithAddressType | null>(null);
    const [parcelCount, setParcelCount] = useState<number>(0);
    const fetchCompletedTmsTaskWaypointDetail = useTransportStore(state => state.fetchCompletedTmsTaskWaypointDetail);
    const getWaypointLoadingParcelCountsById = useTransportStore(state => state.getWaypointLoadingParcelCountsById);

    // 参照 PickupFinishedDetailPage 的实现，直接使用 photosUrls 字段
    const transportDetailPhotos = (() => {
        console.log('TransportFinishedDetailPage - Computing transportDetailPhotos:', {
            hasPhotosUrls: !!waypointData?.photosUrls,
            photosUrlsLength: waypointData?.photosUrls?.length,
            photosUrls: waypointData?.photosUrls,
            hasPhotosS3Keys: !!waypointData?.photosS3Keys,
            photosS3KeysLength: waypointData?.photosS3Keys?.length,
            photosS3Keys: waypointData?.photosS3Keys
        });
        if (waypointData?.photosUrls && waypointData.photosUrls.length > 0) {
            console.log('TransportFinishedDetailPage - Returning photos:', waypointData.photosUrls);
            return waypointData.photosUrls;
        }
        console.log('TransportFinishedDetailPage - Returning empty array');
        return [];
    })();

    console.log('TransportFinishedDetailPage - After computing, transportDetailPhotos:', {
        length: transportDetailPhotos.length,
        photos: transportDetailPhotos
    });

    // 获取途径点详情
    useEffect(() => {
        const fetchWaypointDetail = async () => {
            if (!taskId || !selectedDate || !taskWayPointId) {
                console.warn('TransportFinishedDetailPage - Missing params:', { taskId, selectedDate, taskWayPointId });
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const waypoint = await fetchCompletedTmsTaskWaypointDetail(taskId, taskWayPointId, selectedDate);
                if (waypoint) {
                    setWaypointData(waypoint);
                    // 查询包裹数量
                    if (waypoint.taskWayPointId) {
                        const count = await getWaypointLoadingParcelCountsById(Number(waypoint.taskWayPointId));
                        setParcelCount(count);
                    }
                }
            } catch (error) {
                console.error('TransportFinishedDetailPage - Failed to fetch waypoint detail:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchWaypointDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskWayPointId, taskId, selectedDate]);

    // 显示加载状态
    if (loading && !waypointData) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(TmsPath.TMS_TRANSPORT_FINISHED)}>
                        {t('pages.tms.finished.title')}
                    </NavBar>
                </div>
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-gray-500">{t('pages.tms.finished.loading') || 'Loading...'}</div>
                </div>
            </div>
        );
    }

    // 显示无数据状态
    if (!waypointData && !loading) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(TmsPath.TMS_TRANSPORT_FINISHED)}>
                        {t('pages.tms.finished.title')}
                    </NavBar>
                </div>
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-gray-500">{t('pages.tms.finished.noData')}</div>
                </div>
            </div>
        );
    }

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);


    // 获取站点编号显示文本
    const getSiteCodeText = (): string => {
        const isStartPoint = waypointData?.waypointType === 1;
        const isEndPoint = waypointData?.waypointType === 3;
        if (isStartPoint) {
            return t('pages.tms.start');
        }
        if (isEndPoint) {
            return t('pages.tms.end');
        }
        return waypointData?.sequence?.toString() || '-';
    };

    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => navigate(TmsPath.TMS_TRANSPORT_FINISHED, {
                    state: {
                        selectedDate: selectedDate
                    }
                })}>
                    {waypointData?.wayPointName || '-'}
                </NavBar>
            </div>

            <div className="flex-1 overflow-y-auto bg-gray-100">
                <div className="p-4 bg-white mb-1">
                    <div className="text-lg font-bold mb-4">{t('pages.tms.finished.detailInfo')}</div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.tms.finished.site')}:</div>
                        <div className="text-right break-all">{waypointData?.wayPointName || '-'}</div>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-500">{t('pages.tms.finished.code')}:</div>
                        <div className="text-right">{getSiteCodeText()}</div>
                    </div>
                    {waypointData?.contactName && (
                        <div className="flex justify-between items-center mb-4">
                            <div className="text-gray-500">{t('pages.tms.finished.contact')}:</div>
                            <div className="text-right">{waypointData.contactName}</div>
                        </div>
                    )}
                    {waypointData?.contactPhone && (
                        <div className="flex justify-between items-center mb-4">
                            <div className="text-gray-500">{t('pages.tms.finished.phone')}:</div>
                            <div className="text-right">
                                {`${waypointData.contactPhone}${waypointData.ext ? `-${waypointData.ext}` : ''}`}
                            </div>
                        </div>
                    )}
                    <div className="flex justify-between items-start mb-4">
                        <div className="text-gray-500">{t('pages.tms.finished.address')}:</div>
                        <div className="text-right flex-1 ml-4 break-all">
                            {buildAddress1AndAddress2({
                                addressLine1: waypointData?.addressLine1 ?? undefined,
                                addressLine2: waypointData?.addressLine2 ?? undefined
                            })}
                        </div>
                    </div>
                    { waypointData?.planArrivalTime && (
                        <div className="flex justify-between items-center mb-4">
                            <div className="text-gray-500">{t('pages.tms.finished.scheduledArrivalTime')}:</div>
                            <div className="text-right">
                                {waypointData?.planArrivalTime && (formatTimestampByLanguage(waypointData.planArrivalTime, i18n.language) + (waypointData.planArrivalTimezone ? ` (${waypointData.planArrivalTimezone})` : ''))|| '-'}
                            </div>
                        </div>
                    )}
                    {waypointData?.actualArrivalTime && (
                        <div className="flex justify-between items-center mb-4">
                            <div className="text-gray-500 text-[#6366f1]">{t('pages.tms.finished.actualArrivalTime')}:</div>
                            <div className="text-right text-[#6366f1]">
                                {(formatTimestampByLanguage(waypointData.actualArrivalTime, i18n.language) + (waypointData.actualArrivalTimezone ? ` (${waypointData.actualArrivalTimezone})` : ''))}
                            </div>
                        </div>
                    )}
                    {waypointData?.planDepartureTime && (
                        <div className="flex justify-between items-center mb-4">
                            <div className="text-gray-500">{t('pages.tms.finished.scheduledDepartureTime')}:</div>
                            <div className="text-right">
                                {(formatTimestampByLanguage(waypointData.planDepartureTime, i18n.language) + (waypointData.planDepartureTimezone ? ` (${waypointData.planDepartureTimezone})` : ''))}
                            </div>
                        </div>
                    )}
                    {waypointData?.actualDepartureTime && (
                        <div className="flex justify-between items-center mb-4">
                            <div className="text-gray-500 text-[#6366f1]">{t('pages.tms.finished.actualDepartureTime')}:</div>
                            <div className="text-right text-[#6366f1]">
                                {(formatTimestampByLanguage(waypointData.actualDepartureTime, i18n.language) + (waypointData.actualDepartureTimezone ? ` (${waypointData.actualDepartureTimezone})` : ''))}
                            </div>
                        </div>
                    )}
                    {parcelCount > 0 && (
                        <div className="flex justify-between items-center mb-4">
                            <div className="text-gray-500">{t('pages.tms.finished.quantity')}:</div>
                            <div className="text-right">{parcelCount}</div>
                        </div>
                    )}
                </div>
                
                {transportDetailPhotos.length > 0 && (
                    <div className="p-4 bg-white">
                        <div className="text-lg font-bold mb-4">{t('pages.tms.finished.photos')}</div>
                        {/*<h2 className="text-base font-semibold mb-1">{t('pages.tms.transporting.arriving.photos')}</h2>*/}
                        <p className="text-sm text-gray-600 mb-1">
                            <span>{t('pages.tms.transporting.arriving.photoDesc')}</span>
                            <span
                                className={`text-green-600 text-base`}>{t('pages.tms.transporting.arriving.photoDesc1')}</span>
                            {t('pages.tms.transporting.arriving.punctuation')}
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                            {transportDetailPhotos.map((photo, index) => (
                                <div key={index}
                                     className="aspect-square overflow-hidden bg-white border border-gray-200 rounded-lg">
                                    <Image
                                        src={photo}
                                        alt={t('pages.tms.finished.photoAlt') as string}
                                        fit="cover"
                                        className="w-full h-full object-cover"
                                        onClick={() => handleViewPhoto(photo)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* 查看大图 */}
            <TransportPhotoViewer
                photoUrl={viewingPhoto}
                onClose={() => setViewingPhoto(null)}
            />
        </div>
    );
};

export default TransportFinishedDetailPage;

